import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function GetStartedPage() {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.currentTarget;
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value.toString());
    });
    try {
      await fetch('https://readdy.ai/api/form/d6kem514a3uojp5ffrsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="inline-block bg-amber-100 text-amber-900 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            Let's Get Started
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Tell Us About Your<br />
            <span className="text-amber-900">Hosting Goals</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Answer a few quick questions so we can understand your situation and recommend the best path — whether that's our done-for-you services or our coaching programs.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-6">
          {submitted ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-4xl text-emerald-600"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">You're All Set!</h2>
              <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
                Thank you for reaching out. We've received your information and will be in touch within 24 hours to discuss the best path forward for your STR business.
              </p>
              <Link
                to="/"
                className="inline-flex items-center px-8 py-3 bg-amber-900 text-white rounded-full text-sm font-medium hover:bg-amber-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Back to Home
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          ) : (
            <form
              id="get-started-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-8"
            >
              {/* Personal Info */}
              <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-amber-900 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                  Your Contact Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      placeholder="John"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      placeholder="Smith"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@example.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+1 234 567 8900"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50"
                    />
                  </div>
                </div>
              </div>

              {/* Hosting Status */}
              <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-amber-900 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                  Your Hosting Status
                </h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Which best describes you? *</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {['New Host (just starting)', 'Existing Host (already listing)', 'Investor (looking to invest)'].map((option) => (
                      <label key={option} className="flex items-center p-4 border border-gray-200 rounded-xl cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-all has-[:checked]:border-amber-600 has-[:checked]:bg-amber-50">
                        <input type="radio" name="hosting_status" value={option} required className="mr-3 accent-amber-900" />
                        <span className="text-sm text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Property Details */}
              <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-amber-900 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                  Your Property Details
                </h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Number of Properties</label>
                      <input
                        type="number"
                        name="num_properties"
                        min="0"
                        placeholder="e.g. 2"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50"
                        style={{ MozAppearance: 'textfield' } as React.CSSProperties}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Property Location(s)</label>
                      <input
                        type="text"
                        name="property_location"
                        placeholder="e.g. Lagos, Nigeria"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Monthly Revenue (if applicable)</label>
                      <input
                        type="text"
                        name="current_revenue"
                        placeholder="e.g. $2,000/month"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Occupancy Rate (if applicable)</label>
                      <input
                        type="text"
                        name="occupancy_rate"
                        placeholder="e.g. 60%"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Goals & Challenges */}
              <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-amber-900 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">4</span>
                  Your Goals & Challenges
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Biggest Challenges or Goals *</label>
                    <textarea
                      name="challenges_goals"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="Tell us what's holding you back or what you're trying to achieve..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50 resize-none"
                    />
                    <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">I'm interested in: *</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        'Done-For-You Management',
                        'Co-Hosting Services',
                        'Listing Optimization',
                        'Coaching & Education',
                        'Performance Audit',
                        'Not Sure Yet',
                      ].map((option) => (
                        <label key={option} className="flex items-center p-3 border border-gray-200 rounded-xl cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-all">
                          <input type="checkbox" name="interest" value={option} className="mr-3 accent-amber-900" />
                          <span className="text-sm text-gray-700">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* How They Heard */}
              <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-amber-900 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">5</span>
                  A Few More Details
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">How did you hear about us?</label>
                    <input
                      type="text"
                      name="referral_source"
                      placeholder="e.g. Google, Instagram, Referral..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Contact Method</label>
                      <div className="flex gap-3">
                        {['Email', 'Phone', 'WhatsApp'].map((method) => (
                          <label key={method} className="flex items-center p-3 border border-gray-200 rounded-xl cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-all flex-1 justify-center">
                            <input type="radio" name="contact_method" value={method} className="mr-2 accent-amber-900" />
                            <span className="text-sm text-gray-700">{method}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Best Time to Reach You</label>
                      <input
                        type="text"
                        name="best_time"
                        placeholder="e.g. Weekdays 9am–5pm"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-amber-900 text-white rounded-full text-base font-medium hover:bg-amber-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    Submit My Application
                    <i className="ri-arrow-right-line ml-2"></i>
                  </span>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
